import { useState, useEffect } from 'react';

// storageKey позволяет изолировать окна разных страниц, сохраняя совместимость
export function useDataWindow(numOfVisible = 100, maxDataLength = null, storageKey = 'dataWindow') {
  const [dataWindow, setDataWindow] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Убедимся, что сохранённое окно корректно
        if (typeof parsed.start === 'number' && typeof parsed.end === 'number') {
          return parsed;
        }
      } catch (e) {
        console.warn('Failed to parse saved dataWindow', e);
      }
    }
    // Используем numOfVisible для инициализации
    return { start: 0, end: numOfVisible };
  });

  const updateDataWindow = (newWindow) => {
    // Защищаемся от некорректных значений
    const safeWindow = {
      start: Math.max(0, Number(newWindow.start) || 0),
      end: Math.max(Number(newWindow.start) || 0, Number(newWindow.end) || 0)
    };
    
    const stringified = JSON.stringify(safeWindow);
    localStorage.setItem(storageKey, stringified);
    setDataWindow(safeWindow);
    // Вручную отправляем StorageEvent для синхронизации в той же вкладке
    window.dispatchEvent(
      new StorageEvent('storage', {
        key: storageKey,
        newValue: stringified,
        oldValue: JSON.stringify(dataWindow),
        url: window.location.href,
        storageArea: localStorage,
      })
    );
  };

  // Адаптация окна при изменении numOfVisible или maxDataLength, но сохранение позиции
  useEffect(() => {
    const currentWindowSize = dataWindow.end - dataWindow.start;
    const maxLength = maxDataLength || dataWindow.end;
    
    // Если размер окна изменился, адаптируем его, но сохраняем позицию
    if (currentWindowSize !== numOfVisible) {
      const newStart = Math.max(0, dataWindow.start);
      const newEnd = Math.min(newStart + numOfVisible, maxLength);
      
      // Если новое окно выходит за границы, сдвигаем его
      if (newEnd > maxLength) {
        const adjustedStart = Math.max(0, maxLength - numOfVisible);
        const adjustedEnd = maxLength;
        updateDataWindow({ start: adjustedStart, end: adjustedEnd });
      } else {
        updateDataWindow({ start: newStart, end: newEnd });
      }
    }
    
    // Если данные стали короче, адаптируем окно
    if (maxDataLength && dataWindow.end > maxDataLength) {
      const newStart = Math.max(0, maxDataLength - numOfVisible);
      const newEnd = maxDataLength;
      updateDataWindow({ start: newStart, end: newEnd });
    }
  }, [numOfVisible, maxDataLength]); // Зависимости от numOfVisible и maxDataLength

  // Обработчик внешних изменений (другие вкладки)
  useEffect(() => {
    const handler = (event) => {
      if (event.key === storageKey && event.newValue) {
        try {
          const parsed = JSON.parse(event.newValue);
          if (parsed && typeof parsed.start === 'number' && typeof parsed.end === 'number') {
            setDataWindow(parsed);
          }
        } catch (e) {
          console.warn('Failed to parse incoming dataWindow from storage event', e);
        }
      }
    };

    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [storageKey]);

  return [dataWindow, updateDataWindow];
}